import PropTypes from "prop-types";
import React from "react";

import { SubItem } from "~/components/Subnav";
import {
    MainNavWithSubNav,
    MainNavWithSubNavIcon,
} from "~/components/MainMobileNavigationItem";
import { MainNavLinkDefault } from "~/components/Header";
import ErrorBoundary from "~/components/ErrorBoundary";
import MobileSubNav from "~/components/MobileSubNav";

import trackNavigationEvent from "~/utilities/trackNavigationEvent";
import { sanitizeForId } from "~/utilities/helpers";

import { useTranslationContext } from "~/context/TranslationContext";

const MobileSubNavItem = ({
    clearAllBodyScrollLocks,
    parentLinkLabel,
    parentIndex = -1,
    linkLabel,
    linkUrl,
    linkNativeName = null,
    newTabOverride = "",
    subItems = [],
    subTagRef = null,
    tagRef = null,
    onKeyDown = () => {},
    languageCode,
    openedSubnavs = {},
    dispatch = () => {},
    level = "secondary",
    index = -1,
    firstLinkLabel = "",
    firstLinkLabelIndex = -1,
}) => {
    const hasDropdown = subItems && !!subItems.length;
    const tertiaryLevel = level === "tertiary";
    const secondaryMenuId = `${sanitizeForId(parentLinkLabel)}-${firstLinkLabelIndex}`;
    const tertiaryMenuId = `${sanitizeForId(parentLinkLabel)}-${sanitizeForId(
        linkNativeName ?? linkLabel,
    )}-${index}`;
    const currentMenuId = `${sanitizeForId(firstLinkLabel)}-${sanitizeForId(parentLinkLabel)}-${parentIndex}`;
    const isNavOpened = openedSubnavs.tertiary === tertiaryMenuId;
    const isHidden = tertiaryLevel
        ? openedSubnavs.tertiary !== currentMenuId
        : openedSubnavs.secondary !== secondaryMenuId ||
          (openedSubnavs.secondary === secondaryMenuId &&
              openedSubnavs.tertiary);

    const { activeCode, setActiveCode } = useTranslationContext();
    const textLabel = linkNativeName ?? linkLabel;

    const linkProps = {
        newTabOverride,
        isActive: languageCode === activeCode,
        to: linkUrl,
        onClick: () => {
            setActiveCode(languageCode);
            clearAllBodyScrollLocks();
            trackNavigationEvent(
                false,
                tertiaryLevel ? 3 : 2,
                `${
                    tertiaryLevel && firstLinkLabel
                        ? `${firstLinkLabel} > `
                        : ""
                }${parentLinkLabel} > ${textLabel}`,
                false,
            );
        },
        tagRef: tertiaryLevel ? subTagRef : tagRef,
        onKeyDown,
        hidden: isHidden,
    };
    const buttonProps = {
        onClick: () => {
            trackNavigationEvent(
                true,
                2,
                `${
                    tertiaryLevel && firstLinkLabel
                        ? `${firstLinkLabel} > `
                        : ""
                }${parentLinkLabel} > ${textLabel}`,
                false,
            );
            dispatch({
                type: "HANDLE_SUB_NAV",
                id: tertiaryMenuId,
                currentSubNavs: openedSubnavs,
                level: "tertiary",
            });
        },
        ref: tertiaryLevel ? subTagRef : tagRef,
        type: "button",
        "aria-label": `${linkLabel} submenu`,
        onKeyDown,
        id: tertiaryMenuId,
        hidden: isHidden,
    };

    return (
        <SubItem>
            {hasDropdown > 0 ? (
                <ErrorBoundary>
                    <MainNavWithSubNav {...buttonProps}>
                        {linkLabel}
                        <MainNavWithSubNavIcon name="arrow-right" />
                    </MainNavWithSubNav>

                    <MobileSubNav
                        level="tertiary"
                        isOpen={isNavOpened}
                        items={subItems}
                        parentLinkNativeName={linkNativeName}
                        firstLinkLabel={parentLinkLabel}
                        {...{
                            linkUrl,
                            linkLabel,
                            newTabOverride,
                            clearAllBodyScrollLocks,
                            openedSubnavs,
                            dispatch,
                            subTagRef,
                            index,
                            firstLinkLabelIndex,
                            parentIndex,
                        }}
                    />
                </ErrorBoundary>
            ) : (
                <MainNavLinkDefault {...linkProps}>
                    {linkLabel}
                </MainNavLinkDefault>
            )}
        </SubItem>
    );
};

MobileSubNavItem.propTypes = {
    subItems: PropTypes.arrayOf(PropTypes.shape({})),
    clearAllBodyScrollLocks: PropTypes.func.isRequired,
    parentLinkLabel: PropTypes.string.isRequired,
    parentIndex: PropTypes.number,
    linkLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
        .isRequired,
    linkUrl: PropTypes.string.isRequired,
    linkNativeName: PropTypes.string,
    newTabOverride: PropTypes.string,
    tagRef: PropTypes.object,
    subTagRef: PropTypes.object,
    onKeyDown: PropTypes.func,
    isActive: PropTypes.bool,
    openedSubnavs: PropTypes.shape({}),
    dispatch: PropTypes.func,
    level: PropTypes.string,
    firstLinkLabel: PropTypes.string,
    index: PropTypes.number,
    firstLinkLabelIndex: PropTypes.number,
};

export default MobileSubNavItem;
